import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Router, Wifi, WifiOff, AlertTriangle, Server, Search, X, RefreshCw, Plus, MoreVertical,
  Info, Settings, Tag, UploadCloud, RotateCcw, Power, PowerOff, Trash2, Edit2, Check, ChevronRight,
  Download, Ruler, User, Zap,
} from 'lucide-react';
import { oltAPI } from '../../services/api';
import { AppSheet, SheetRow, AppFold } from '../../components/common/AppViewKit';
import { SignalBadge, isOnuEnabled, fmtBytes } from '../OLTPage';
import { useAuth } from '../../context/AuthContext';
import './oltMobile.css';

// Same cut-offs as the web view's SignalBadge: below -24 dBm is weak, below -27 is bad.
const WEAK_DBM = -24;
const PAGE = 60;

const isOnline = o => (o.status || '').toLowerCase().includes('online') || o.online === true;
const rxOf = o => o.rxPower ?? o.rx_power ?? o.opticalRx;
const txOf = o => o.txPower ?? o.tx_power ?? o.opticalTx;
const isWeak = o => { const v = parseFloat(rxOf(o)); return isOnline(o) && Number.isFinite(v) && v < WEAK_DBM; };
const reasonOf = o => (o.deregisterReason || '').trim() || 'Unknown';
const clock = ts => (ts ? new Date(ts).toLocaleTimeString('en-IN', { hour12: false }) : null);
const labelOf = o => o.label || o.name || o.description || '';
// the OLT prints 'N/A' for an empty description
const cleanLabel = o => { const l = labelOf(o); return l === 'N/A' ? '' : l; };
const toneOf = o => (!isOnline(o) ? 'om-bad' : isWeak(o) ? 'om-warn' : !isOnuEnabled(o) ? 'om-off' : 'om-ok');

function Empty({ icon: Icon = Wifi, title, children }) {
  return (
    <div className="om-empty">
      <Icon size={30} />
      <b>{title}</b>
      {children}
    </div>
  );
}

function Skeleton({ n = 6 }) {
  return <div className="om-list">{Array.from({ length: n }, (_, i) => <div key={i} className="om-skel" />)}</div>;
}

function ErrorBlock({ message, onRetry, stale }) {
  return (
    <div className="om-banner om-bad" role="alert">
      <AlertTriangle size={18} />
      <span>{stale ? `Could not refresh: ${message}. Showing the last data from ${stale}.` : message}</span>
      <button className="om-btn" onClick={onRetry}>Retry</button>
    </div>
  );
}

// ── Label editor (same call as the web view's LabelCell) ──────────────────────
function LabelEditor({ onu, canEdit, onSaved }) {
  const current = cleanLabel(onu);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (value === current) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await oltAPI.saveONULabel(onu.id, value);
      onSaved(onu.id, value);
      if (res.data?.oltSynced === false) toast.error(res.data?.message || 'Label saved locally, but OLT was not updated', { duration: 6000 });
      else toast.success(res.data?.message || 'Label saved');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="om-edit">
        <input value={value} autoFocus onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); else if (e.key === 'Escape') setEditing(false); }}
          aria-label="ONU label" />
        <button className="om-iconbtn" onClick={save} disabled={saving} aria-label="Save label"><Check size={18} /></button>
        <button className="om-iconbtn" onClick={() => setEditing(false)} aria-label="Cancel"><X size={18} /></button>
      </div>
    );
  }
  return (
    <div className="om-label">
      <b>{current || <span className="om-muted" style={{ fontWeight: 400 }}>No label</span>}</b>
      {canEdit && (
        <button className="om-iconbtn" onClick={() => { setValue(current); setEditing(true); }} aria-label="Edit label"><Edit2 size={16} /></button>
      )}
    </div>
  );
}

// ── ONU detail sheet ──────────────────────────────────────────────────────────
function OnuSheet({ onu, onClose, isAdmin, canOpenCustomer, actions, busy }) {
  const [distance, setDistance] = useState(undefined); // undefined = not asked, null = no answer
  const [measuring, setMeasuring] = useState(false);
  const online = isOnline(onu);
  const enabled = isOnuEnabled(onu);
  const label = cleanLabel(onu);

  // Distance is a live probe of this one ONU on the OLT, so it only runs when asked.
  const measure = async () => {
    setMeasuring(true);
    try {
      const { data } = await oltAPI.getONUDetail(onu.slot, onu.port, onu.onuId);
      setDistance(data?.data?.distance ?? null);
    } catch (err) {
      setDistance(null);
      toast.error(err.response?.data?.message || 'Could not read distance');
    } finally { setMeasuring(false); }
  };

  const kv = rows => (
    <dl className="om-kv">
      {rows.filter(([, v]) => v != null && v !== '').map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
    </dl>
  );

  return (
    <AppSheet title={label || onu.id} onClose={onClose}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        <span className={`om-tag ${online ? 'om-ok' : 'om-bad'}`}>{online ? 'Online' : 'Offline'}</span>
        {!enabled && <span className="om-tag om-off">Disabled</span>}
        {isWeak(onu) && <span className="om-tag om-warn">Weak signal</span>}
        {!online && <span className="om-tag om-bad">{reasonOf(onu)}</span>}
      </div>

      <LabelEditor onu={onu} canEdit={isAdmin} onSaved={actions.labelSaved} />
      {label && canOpenCustomer && (
        <Link to={`/customers?search=${encodeURIComponent(label)}`} className="om-btn" style={{ marginBottom: 10, textDecoration: 'none' }}>
          <User size={16} /> Open customer <ChevronRight size={16} />
        </Link>
      )}

      <div className="om-metrics">
        <div className="om-metric"><small>ONU Rx</small><SignalBadge dbm={rxOf(onu)} /></div>
        <div className="om-metric"><small>ONU Tx</small><SignalBadge dbm={txOf(onu)} /></div>
        <div className="om-metric"><small>OLT Rx</small><SignalBadge dbm={onu.rxPowerOlt} /></div>
      </div>
      <div style={{ marginBottom: 8 }}>
        {distance === undefined
          ? <button className="om-btn" onClick={measure} disabled={measuring}><Ruler size={16} /> {measuring ? 'Measuring…' : 'Measure distance'}</button>
          : <span className="om-muted" style={{ fontSize: '.82rem' }}>Distance: <b style={{ color: 'var(--text)' }}>{distance != null ? `${distance} m` : 'no answer from OLT'}</b></span>}
      </div>

      <AppFold isAppView title="Status and history" summary={online ? `Alive ${onu.aliveTime || '—'}` : `Last down ${onu.lastDeregister || '—'}`} defaultOpen={!online}>
        {kv([
          ['Admin state', onu.adminState], ['OMCC state', onu.omccState], ['Phase', onu.phaseState],
          ['Alive time', onu.aliveTime], ['Last register', onu.lastRegister],
          ['Last deregister', onu.lastDeregister], ['Reason', onu.deregisterReason],
        ])}
      </AppFold>
      <AppFold isAppView title="Device" summary={onu.serialNumber || onu.id}>
        {kv([
          ['GPON ID', onu.id], ['Serial', onu.serialNumber], ['Model', onu.model], ['Type', onu.profile],
          ['Line profile', onu.linePro], ['Service profile', onu.srvPro],
        ])}
      </AppFold>

      {isAdmin && (
        <div style={{ marginTop: 8 }}>
          <SheetRow icon={Settings} label="ONU config (OLT interface)" onClick={() => actions.openOltConfig(onu)} />
          <SheetRow icon={Tag} label="Line / service profile" onClick={() => actions.openProfile(onu)} />
          <SheetRow icon={UploadCloud} label="Upgrade firmware" onClick={() => actions.openUpgrade(onu)} />
          <SheetRow icon={RotateCcw} label="Restart ONU" onClick={() => actions.reboot(onu)} />
          <SheetRow icon={enabled ? PowerOff : Power} label={enabled ? 'Deactivate ONU' : 'Activate ONU'}
            disabled={!!busy.deactivating[onu.id]} onClick={() => actions.toggleState(onu)} />
          <SheetRow icon={Trash2} label="Deregister" danger onClick={() => actions.deregister(onu)} />
        </div>
      )}
    </AppSheet>
  );
}

// ── Preview/result of "Match Blank ONUs by MAC", as cards ─────────────────────
const MATCH_STATUS = {
  proposed: ['Match', 'om-ok'], labelled: ['Labelled', 'om-ok'], no_match: ['No match', 'om-off'],
  ambiguous: ['Several customers', 'om-warn'], conflict: ['Conflict', 'om-warn'], write_failed: ['Write failed', 'om-bad'],
};
function MacMatchSheet({ result, selected, onToggle, onApply, applying, onClose }) {
  const plan = result.plan || [];
  const order = { proposed: 0, labelled: 0, write_failed: 1, conflict: 2, ambiguous: 3, no_match: 4 };
  const rows = [...plan].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  const proposed = plan.filter(p => p.status === 'proposed').length;
  return (
    <AppSheet title={result.preview ? 'Match blank ONUs: preview' : 'Match blank ONUs: result'} onClose={applying ? () => {} : onClose}>
      <p className="om-muted" style={{ fontSize: '.8rem', margin: '0 0 8px' }}>
        {result.blankCount} online ONU(s) blank or named after a closed/unknown customer.{' '}
        <b style={{ color: 'var(--success)' }}>{result.preview ? `${proposed} can be matched.` : `${result.labelled} labelled.`}</b>
        {result.aborted && <b style={{ color: 'var(--danger)' }}> {result.aborted}</b>}
      </p>
      {result.preview && proposed > 0 && (
        <button className="om-btn primary" style={{ width: '100%', marginBottom: 6 }} onClick={onApply} disabled={applying || !selected.length}>
          {applying ? 'Labelling…' : `Apply ${selected.length} selected`}
        </button>
      )}
      {rows.map(p => {
        const [text, tone] = MATCH_STATUS[p.status] || [p.status, 'om-off'];
        return (
          <div className="om-match" key={p.onuId}>
            {result.preview && p.status === 'proposed'
              ? <input type="checkbox" checked={selected.includes(p.onuId)} onChange={() => onToggle(p.onuId)} disabled={applying} aria-label={`Select ${p.onuId}`} />
              : <span style={{ width: 22, flex: '0 0 22px' }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <b className="om-mono">{p.onuId}</b> <span className={`om-tag ${tone}`}>{text}</span>
              <div className="om-muted om-mono">{p.serialNumber}</div>
              <div style={{ color: p.currentLabel ? 'var(--warning)' : 'var(--text-muted)' }}>
                {p.currentLabel ? `now "${p.currentLabel}": ${p.why}` : 'blank'}
              </div>
              {p.username && <div><b>{p.username}</b> <span className="om-muted">{p.customerName}</span></div>}
              <div className="om-muted om-mono">
                {p.mac || (p.macs || []).join(', ') || '—'}
                {p.matchSource && ` · ${p.matchSource === 'live' ? 'live session' : 'Netzur stored'}`}
              </div>
              {p.reason && <div className="om-muted">{p.reason}</div>}
            </div>
          </div>
        );
      })}
    </AppSheet>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function OltMobileView(props) {
  const {
    isAdmin, inTechnical, configured, sysInfo, updated, errors, onus, ports, gePorts, alarms, loading,
    tab, setTab, search, setSearch, clearSerialParam, onRefresh, actions, busy,
    fetchAllResult, clearFetchAll, macResult, macSelected, clearMac, toggleMac,
  } = props;
  const { hasModuleRight } = useAuth();
  const canOpenCustomer = !inTechnical && hasModuleRight('customers', 'view');

  const [filter, setFilter] = useState('all');        // all | online | offline | weak
  const [reason, setReason] = useState(null);         // offline reason chip
  const [port, setPort] = useState('all');
  const [shown, setShown] = useState(PAGE);
  const [selectedId, setSelectedId] = useState(null);
  const [sheet, setSheet] = useState(null);           // 'device' | 'tools' | 'ports'
  const [openGe, setOpenGe] = useState(null);

  useEffect(() => { setShown(PAGE); }, [filter, reason, port, search]);

  const offlineList = useMemo(() => onus.filter(o => !isOnline(o)), [onus]);
  const counts = useMemo(() => ({
    total: onus.length, online: onus.length - offlineList.length, offline: offlineList.length, weak: onus.filter(isWeak).length,
  }), [onus, offlineList]);
  const reasons = useMemo(() => {
    const m = {};
    for (const o of offlineList) m[reasonOf(o)] = (m[reasonOf(o)] || 0) + 1;
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [offlineList]);
  const portStats = useMemo(() => {
    const m = {};
    for (const o of onus) {
      if (o.port == null) continue;
      m[o.port] = m[o.port] || { total: 0, online: 0 };
      m[o.port].total++;
      if (isOnline(o)) m[o.port].online++;
    }
    return m;
  }, [onus]);

  const list = useMemo(() => {
    const q = search.toLowerCase();
    const rank = o => (!isOnline(o) ? 0 : isWeak(o) ? 1 : 2);
    return onus.filter(o => {
      if (filter === 'online' && !isOnline(o)) return false;
      if (filter === 'offline' && isOnline(o)) return false;
      if (filter === 'weak' && !isWeak(o)) return false;
      if (reason && (isOnline(o) || reasonOf(o) !== reason)) return false;
      if (port !== 'all' && o.port !== +port) return false;
      if (!q) return true;
      return (o.serialNumber || '').toLowerCase().includes(q) || (o.model || '').toLowerCase().includes(q)
        || labelOf(o).toLowerCase().includes(q) || (o.id || '').toLowerCase().includes(q) || String(o.port || '').includes(q);
    }).sort((a, b) => rank(a) - rank(b));
  }, [onus, filter, reason, port, search]);

  const selected = selectedId ? onus.find(o => o.id === selectedId) : null;
  const pick = f => { setTab('onus'); setReason(null); setFilter(cur => (cur === f ? 'all' : f)); };

  const feed = { onus: 'onus', ports: 'ports', ge: 'ge', alarms: 'alarms', sysinfo: 'status' }[tab] || 'onus';
  const busyNow = loading[tab] || false;
  const stamp = clock(updated[feed]);

  // ── whole-screen states ──
  if (configured === null) {
    return <div className="om"><div className="om-card om-empty"><div className="spinner" style={{ margin: '0 auto 12px' }} />Connecting to OLT…</div></div>;
  }
  if (configured === false) {
    return (
      <div className="om">
        <div className="om-card">
          <Empty icon={Server} title={errors.status ? 'Cannot reach the OLT' : 'OLT not configured'}>
            <p style={{ fontSize: '.85rem', margin: '0 0 14px' }}>
              {errors.status || 'Enter the OLT connection details in Settings to get started.'}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="om-btn" onClick={onRefresh.status}><RefreshCw size={16} /> Retry</button>
              {isAdmin && <Link to="/settings" className="om-btn primary" style={{ textDecoration: 'none' }}><Settings size={16} /> Settings</Link>}
            </div>
          </Empty>
        </div>
      </div>
    );
  }

  const refreshNow = () => {
    (onRefresh[tab] || onRefresh.onus)();
  };

  const sysEntries = sysInfo ? Object.entries(sysInfo).filter(([k]) => !['hostname', 'model', 'temperature', 'cpuUsage', 'memoryUsage', 'uptime'].includes(k)) : [];
  const vital = (k, v) => <div className="om-vital"><small>{k}</small><b>{v || '—'}</b></div>;
  const tabs = [['onus', 'ONUs'], ['ports', 'PON'], ['ge', 'Uplinks'], ['alarms', 'Alarms']];

  return (
    <div className="om">
      {/* OLT identity + device health: the one place these values live */}
      <div className="om-card om-head">
        <div className="om-head-row">
          <span className={`om-dot ${errors.status ? 'om-bad' : 'om-ok'}`} />
          <button className="om-head-title" onClick={() => setSheet('device')} aria-label="Device details">
            <b>{sysInfo?.hostname || sysInfo?.model || 'SYROTECH OLT'}</b>
            <span>{[sysInfo?.model !== sysInfo?.hostname && sysInfo?.model, 'tap for device details'].filter(Boolean).join(' · ')}</span>
          </button>
          <button className="om-iconbtn" onClick={refreshNow} disabled={busyNow} aria-label="Refresh">
            <RefreshCw size={18} className={busyNow ? 'om-spin' : ''} />
          </button>
          {isAdmin && <button className="om-iconbtn" onClick={() => setSheet('tools')} aria-label="More actions"><MoreVertical size={18} /></button>}
        </div>
        {sysInfo && (
          <div className="om-vitals">
            {vital('Temp', sysInfo.temperature)}{vital('CPU', sysInfo.cpuUsage)}{vital('Memory', sysInfo.memoryUsage)}{vital('Uptime', sysInfo.uptime)}
          </div>
        )}
        <div className="om-stamp">
          {errors[feed] ? <span style={{ color: 'var(--danger)' }}>Last refresh failed</span> : null}
          {stamp ? <span>Updated {stamp}</span> : <span>Not loaded yet</span>}
        </div>
      </div>

      {/* Network health: also the ONU filter */}
      {counts.total > 0 && (
        <div className="om-card om-health">
          <div className="om-bar" aria-hidden>
            <i style={{ width: `${counts.online / counts.total * 100}%`, background: 'var(--success)' }} />
            <i style={{ width: `${counts.offline / counts.total * 100}%`, background: 'var(--danger)' }} />
          </div>
          <div className="om-stats">
            <button className="om-stat om-ok" aria-pressed={filter === 'online'} onClick={() => pick('online')}><b>{counts.online}</b><span>Online of {counts.total}</span></button>
            <button className="om-stat om-bad" aria-pressed={filter === 'offline'} onClick={() => pick('offline')}><b>{counts.offline}</b><span>Offline</span></button>
            <button className="om-stat om-warn" aria-pressed={filter === 'weak'} onClick={() => pick('weak')}><b>{counts.weak}</b><span>Weak signal</span></button>
          </div>
          {reasons.length > 0 && (
            <div className="om-reasons">
              Offline cause:
              {reasons.map(([r, n]) => (
                <button key={r} className="om-chip om-bad" aria-pressed={reason === r}
                  onClick={() => { setTab('onus'); setFilter('offline'); setReason(cur => (cur === r ? null : r)); }}>
                  {r} · {n}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="om-card om-tabs" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className="om-tab" onClick={() => setTab(k)}>
            {label}{k === 'alarms' && alarms.length > 0 && <em>{alarms.length}</em>}
          </button>
        ))}
      </div>

      {/* ── ONUs ── */}
      {tab === 'onus' && (
        <>
          <div className="om-tools">
            <div className="om-search">
              <Search size={16} />
              <input value={search} placeholder="Serial, name, GPON ID…" inputMode="search" enterKeyHint="search"
                onChange={e => { setSearch(e.target.value); if (!e.target.value) clearSerialParam(); }} />
              {search && <button onClick={() => { setSearch(''); clearSerialParam(); }} aria-label="Clear search"><X size={16} /></button>}
            </div>
            <button className="om-btn" onClick={() => setSheet('ports')} aria-pressed={port !== 'all'}>
              <Router size={16} />{port === 'all' ? 'All PON' : `PON ${port}`}
            </button>
            {isAdmin && <button className="om-btn primary" onClick={actions.openProvision} aria-label="Provision ONU"><Plus size={18} /></button>}
          </div>

          {fetchAllResult && (
            <div className="om-banner om-ok">
              <span>
                <b>{fetchAllResult.updated} updated</b>
                {fetchAllResult.unmatched > 0 && `, ${fetchAllResult.unmatched} unmatched`} of {fetchAllResult.total} broadband customers
                {fetchAllResult.errors?.length > 0 && (
                  <details><summary>{fetchAllResult.errors.length} errors</summary>
                    {fetchAllResult.errors.map((e, i) => <div key={i}><b>{e.customer}</b>: {e.error}</div>)}
                  </details>
                )}
              </span>
              <button className="om-iconbtn" onClick={clearFetchAll} aria-label="Dismiss"><X size={16} /></button>
            </div>
          )}

          {errors.onus && <ErrorBlock message={errors.onus} onRetry={onRefresh.onus} stale={onus.length ? clock(updated.onus) : null} />}

          {loading.onus && !onus.length ? <Skeleton /> : list.length === 0 && !errors.onus ? (
            <div className="om-card">
              <Empty icon={onus.length ? Search : Wifi} title={onus.length ? 'No ONUs match' : 'No ONUs found on this OLT'}>
                {onus.length > 0 && (
                  <button className="om-btn" onClick={() => { setFilter('all'); setReason(null); setPort('all'); setSearch(''); clearSerialParam(); }}>Clear filters</button>
                )}
              </Empty>
            </div>
          ) : (
            <>
              <div className="om-count">{list.length}{list.length !== counts.total ? ` of ${counts.total}` : ''} ONUs · problems first</div>
              <div className="om-list">
                {list.slice(0, shown).map(o => {
                  const on = isOnline(o);
                  const label = cleanLabel(o);
                  return (
                    <button key={o.id} className={`om-card om-row ${toneOf(o)}`} onClick={() => setSelectedId(o.id)}>
                      <div className="om-row-main">
                        <b>{label || <span className="om-muted" style={{ fontWeight: 400 }}>No label</span>}</b>
                        <small className="om-mono">{o.id}{o.serialNumber ? ` · ${o.serialNumber}` : ''}</small>
                        {(!on || !isOnuEnabled(o)) && (
                          <div className="om-row-why">
                            {!on && <span className="om-tag om-bad">{reasonOf(o)}</span>}
                            {!isOnuEnabled(o) && <span className="om-tag om-off">Disabled</span>}
                            {!on && o.lastDeregister && <small>since {o.lastDeregister}</small>}
                          </div>
                        )}
                      </div>
                      <div className="om-row-side">
                        <SignalBadge dbm={rxOf(o)} />
                        <ChevronRight size={16} color="var(--text-muted)" />
                      </div>
                    </button>
                  );
                })}
              </div>
              {list.length > shown && (
                <button className="om-btn" onClick={() => setShown(n => n + PAGE)}>Show {Math.min(PAGE, list.length - shown)} more</button>
              )}
            </>
          )}
        </>
      )}

      {/* ── PON ports ── */}
      {tab === 'ports' && (
        <>
          {errors.ports && <ErrorBlock message={errors.ports} onRetry={onRefresh.ports} stale={ports.length ? clock(updated.ports) : null} />}
          {loading.ports && !ports.length ? <Skeleton n={4} /> : ports.length === 0 && !errors.ports ? (
            <div className="om-card"><Empty icon={Router} title="No port data" /></div>
          ) : (
            <div className="om-grid">
              {ports.map((p, i) => {
                const n = p.port ?? p.portId ?? p.id ?? i;
                const total = p.totalOnus ?? p.onuCount ?? 0;
                const up = p.onlineOnus ?? p.activeOnus ?? 0;
                const down = Math.max(total - up, 0);
                const st = (p.status || '').toLowerCase();
                const portUp = st.includes('up') || st.includes('active');
                const tone = !portUp || (total > 0 && up === 0) ? 'om-bad' : down > 0 ? 'om-warn' : 'om-ok';
                return (
                  <button key={p.id || p.portId || i} className={`om-card om-pon ${tone}`}
                    onClick={() => { setPort(String(n)); setFilter('all'); setReason(null); setTab('onus'); }}>
                    <header><b>PON {n}</b><span className={`om-tag ${portUp ? 'om-ok' : 'om-bad'}`}>{p.status || '—'}</span></header>
                    <div className="om-bar" aria-hidden>
                      <i style={{ width: `${total ? up / total * 100 : 0}%`, background: 'var(--success)' }} />
                      <i style={{ width: `${total ? down / total * 100 : 0}%`, background: 'var(--danger)' }} />
                    </div>
                    <small><b style={{ color: 'var(--text)' }}>{up}</b> of {total} online{down > 0 && <b style={{ color: 'var(--danger)' }}> · {down} offline</b>}</small>
                    <small style={{ marginTop: 4 }}>Avg Rx <SignalBadge dbm={p.avgRxPower ?? p.avgRx} /></small>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── GE uplinks ── */}
      {tab === 'ge' && (
        <>
          {errors.ge && <ErrorBlock message={errors.ge} onRetry={onRefresh.ge} stale={clock(updated.ge)} />}
          {loading.ge && !updated.ge ? <Skeleton n={4} /> : (
            <div className="om-grid">
              {gePorts.map(p => {
                const upNow = (p.status || '').toLowerCase() === 'up';
                const unknown = p.status === 'unknown';
                const open = openGe === p.id;
                const hasTraffic = p.rxBytes != null || p.txBytes != null;
                return (
                  <button key={p.id} className={`om-card om-pon ${unknown ? 'om-off' : upNow ? 'om-ok' : 'om-bad'}`}
                    style={open ? { gridColumn: '1 / -1' } : undefined} onClick={() => setOpenGe(open ? null : p.id)} aria-expanded={open}>
                    <header>
                      <b>{p.name || p.id}</b>
                      <span className={`om-tag ${unknown ? 'om-off' : upNow ? 'om-ok' : 'om-bad'}`}>{unknown ? 'NO DATA' : upNow ? 'UP' : 'DOWN'}</span>
                    </header>
                    {p.name && <small>{p.id}</small>}
                    {p.speed && <small>{p.speed}</small>}
                    {p.errors > 0 && <small style={{ color: 'var(--danger)' }}>{p.errors} errors</small>}
                    {open && (
                      hasTraffic ? (
                        <dl className="om-kv" style={{ padding: '8px 0 0' }}>
                          <dt>Rx</dt><dd>{p.rxBytes != null ? fmtBytes(p.rxBytes) : '—'}{p.rxPkts != null && ` · ${p.rxPkts.toLocaleString()} pkts`}</dd>
                          <dt>Tx</dt><dd>{p.txBytes != null ? fmtBytes(p.txBytes) : '—'}{p.txPkts != null && ` · ${p.txPkts.toLocaleString()} pkts`}</dd>
                          <dt>Errors</dt><dd>{p.errors || 0}</dd>
                        </dl>
                      ) : <small style={{ marginTop: 6 }}>No traffic data</small>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Alarms ── */}
      {tab === 'alarms' && (
        <>
          {errors.alarms && <ErrorBlock message={errors.alarms} onRetry={onRefresh.alarms} stale={clock(updated.alarms)} />}
          {loading.alarms && !updated.alarms ? <Skeleton n={3} /> : alarms.length === 0 && !errors.alarms ? (
            <div className="om-card">
              <Empty icon={Zap} title="No active alarms">
                <p style={{ fontSize: '.82rem', margin: '0 0 12px' }}>
                  The OLT reports no alarms. ONUs that dropped off (LOS, dying gasp and so on) are listed with their cause under ONUs.
                </p>
                {counts.offline > 0 && <button className="om-btn" onClick={() => pick('offline')}><WifiOff size={16} /> Show {counts.offline} offline ONUs</button>}
              </Empty>
            </div>
          ) : (
            <div className="om-list">
              {alarms.map((a, i) => {
                const sev = (a.severity || a.level || '').toLowerCase();
                const tone = sev === 'critical' || sev === 'major' ? 'om-bad' : sev === 'minor' ? 'om-warn' : 'om-off';
                return (
                  <div key={a.id || i} className={`om-card om-row ${tone}`} style={{ cursor: 'default' }}>
                    <div className="om-row-main">
                      <b style={{ whiteSpace: 'normal' }}>{a.type || a.alarmType || 'Alarm'}</b>
                      <small style={{ whiteSpace: 'normal' }}>{a.description || a.detail || '—'}</small>
                      <small>{a.source || a.object || '—'} · {a.time || a.raisedAt || '—'}</small>
                    </div>
                    <span className={`om-tag ${tone}`}>{a.severity || a.level || '—'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Sheets ── */}
      {selected && (
        <OnuSheet onu={selected} onClose={() => setSelectedId(null)} isAdmin={isAdmin}
          canOpenCustomer={canOpenCustomer} actions={actions} busy={busy} />
      )}

      {sheet === 'ports' && (
        <AppSheet title="Filter by PON port" onClose={() => setSheet(null)}>
          <SheetRow label={`All PON ports (${counts.total} ONUs)`} onClick={() => { setPort('all'); setSheet(null); }} />
          {Object.keys(portStats).map(Number).sort((a, b) => a - b).map(p => (
            <SheetRow key={p} label={`PON ${p}: ${portStats[p].online}/${portStats[p].total} online`}
              onClick={() => { setPort(String(p)); setSheet(null); }} />
          ))}
        </AppSheet>
      )}

      {sheet === 'device' && (
        <AppSheet title="Device details" onClose={() => setSheet(null)}>
          {sysEntries.length === 0
            ? <Empty icon={Info} title="No system info loaded" />
            : (
              <dl className="om-kv" style={{ gridTemplateColumns: '130px 1fr' }}>
                {sysEntries.map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt style={{ textTransform: 'capitalize' }}>{k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</dt>
                    <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          <button className="om-btn" style={{ width: '100%', marginTop: 10 }} onClick={onRefresh.status}>
            <RefreshCw size={16} /> Reload device info{updated.status ? ` (loaded ${clock(updated.status)})` : ''}
          </button>
        </AppSheet>
      )}

      {sheet === 'tools' && isAdmin && (
        <AppSheet title="OLT actions" onClose={() => setSheet(null)}>
          <SheetRow icon={Download} label={busy.fetchAll ? 'Fetching…' : 'Fetch all broadband OLT data'} disabled={busy.fetchAll}
            onClick={() => { setSheet(null); setTab('onus'); actions.fetchAll(); }} />
          <SheetRow icon={Download} label={busy.matchMac ? 'Checking…' : 'Match blank ONUs by MAC'} disabled={busy.matchMac || busy.applyMac}
            onClick={() => { setSheet(null); actions.matchMac(); }} />
          <SheetRow icon={Download} label={busy.saveConfig ? 'Saving…' : 'Download OLT config backup'} disabled={busy.saveConfig}
            onClick={() => { setSheet(null); actions.saveConfig(); }} />
        </AppSheet>
      )}

      {macResult && (
        <MacMatchSheet result={macResult} selected={macSelected} onToggle={toggleMac}
          onApply={actions.applyMac} applying={busy.applyMac} onClose={clearMac} />
      )}
    </div>
  );
}
